import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftRight, MapPin, Ruler, Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import Footer from '../components/Footer';

// Images avant/après
const IMAGES = {
  parquet_avant1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/l8fn6yjc_IMG_20220820_162918.jpg',
  parquet_avant2: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/r8m9giir_IMG_20220820_162918%281%29.jpg',
  parquet_avant3: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/1dtuxo07_IMG_20220820_162832.jpg',
  parquet_apres1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/537j8m6a_IMG_20220820_164728.jpg',
  parquet_apres2: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/qrd3uw50_IMG_20220820_164736.jpg',
  sdb_avant1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/gyeid9f5_Snapchat-926234349.jpg',
  sdb_avant2: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/o22jxxa2_Snapchat-1505124951.jpg',
  sdb_apres1: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/bv8di3wb_Snapchat-1444918410.jpg',
  sdb_apres2: 'https://customer-assets.emergentagent.com/job_aldroubi-luxury/artifacts/pcjnzmvg_Snapchat-1844453526.jpg',
};

const GalleryPage = () => {
  const [filter, setFilter] = useState('all');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedItem(null);
    };
    if (selectedItem) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedItem]);

  const gallery = [
    {
      id: '1',
      title: 'Rénovation Salle de Bain Complète',
      description: 'Installation complète avec pose carrelage, douche et sanitaires.',
      service_type: 'peinture',
      location: 'Argentan',
      surface: '12 m²',
      date: '2024',
      before_image: IMAGES.sdb_avant1,
      after_image: IMAGES.sdb_apres1,
    },
    {
      id: '2',
      title: 'Salle de Bain Moderne',
      description: 'Transformation complète avec carrelage grand format.',
      service_type: 'peinture',
      location: 'Argentan',
      surface: '15 m²',
      date: '2024',
      before_image: IMAGES.sdb_avant2,
      after_image: '/images/MFR - Argentan/Apres-MFR/Snapchat-2079464147.jpg',
    },
    {
      id: '3',
      title: 'Pose Parquet Point de Hongrie',
      description: 'Installation de parquet chevron dans un salon & Ragreage.',
      service_type: 'Revetement de sols',
      location: 'Argentan',
      surface: '70 m²',
      date: 'Août 2022',
      before_image: '/images/avant-escalier-trun.jpeg',
      after_image: IMAGES.parquet_apres1,
    },
    {
      id: '4',
      title: 'Rénovation Séjour Complet',
      description: 'Pose de parquet chevron sur l\'ensemble du rez-de-chaussée & Ragreage.',
      service_type: 'Revetement de sols',
      location: 'Trun',
      surface: '70 m²',
      date: 'Août 2022',
      before_image: '/images/avant-escalier-trun.jpeg',
      after_image: IMAGES.parquet_apres2,
    },

    {
      id: '6',
      title: 'Installation Électrique Complète',
      description: 'Renovation tableau électrique et mise aux normes.',
      service_type: 'electricite',
      location: 'Argentan',
      surface: '120 m²',
      date: '2023',
      before_image: '/images/avant tableau electrique.jpeg',
      after_image: '/images/apres tableau electrique.jpeg',
    },

    {
      id: '8',
      title: 'Rénovation Salon & Escalier',
      description: 'Rénovation complète : électricité, peinture et éclairage.',
      service_type: 'peinture',
      location: 'Caen',
      surface: '50 m²',
      date: '2026',
      before_image: '/images/peinture-escalier-avant.jpeg',
      after_image: '/images/peinture-escalier-apres.jpeg',
    },
  ];

  const filteredGallery = filter === 'all' 
    ? gallery 
    : gallery.filter(item => item.service_type === filter);

  const filters = [
    { id: 'all', label: 'Tous' },
    { id: 'electricite', label: 'Électricité' },
    { id: 'Revetement de sols', label: 'Revêtements de sols' },
    { id: 'peinture', label: 'Peinture' },
  ];

  const SERVICE_TYPE_MAP = {
    electricite: { label: 'Électricité', classes: 'bg-yellow-500/90 text-black' },
    peinture: { label: 'Peinture', classes: 'bg-gold text-black' },
    'Revetement de sols': { label: 'Revêtements de sols', classes: 'bg-gold text-black' },
  };

  const currentIndex = selectedItem ? filteredGallery.findIndex(item => item.id === selectedItem.id) : -1;

  const goToNext = () => {
    if (currentIndex < filteredGallery.length - 1) {
      setSelectedItem(filteredGallery[currentIndex + 1]);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setSelectedItem(filteredGallery[currentIndex - 1]);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12" data-testid="gallery-page">
      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative max-w-6xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button (visible on mobile) */}
              <button
                onClick={() => setSelectedItem(null)}
                aria-label="Fermer"
                className="absolute top-4 right-4 z-30 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation arrows removed per UX request (no cycling) */}
              
              {/* Images */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Show APRÈS first, then AVANT */}
                <motion.div 
                  className="relative rounded-lg overflow-hidden"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <img src={selectedItem.after_image} alt="Après" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 bg-green-500 text-white px-4 py-2 rounded font-bold text-lg">
                    APRÈS
                  </div>
                </motion.div>
                <motion.div 
                  className="relative rounded-lg overflow-hidden"
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <img src={selectedItem.before_image} alt="Avant" className="w-full h-auto" />
                  <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded font-bold text-lg">
                    AVANT
                  </div>
                </motion.div>
              </div>
              
              {/* Info */}
              <motion.div 
                className="mt-6 text-center"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="font-serif text-2xl sm:text-3xl text-white">{selectedItem.title}</h3>
                <p className="text-gray-400 mt-2">{selectedItem.description}</p>
                <div className="flex justify-center gap-6 mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {selectedItem.location}</span>
                  <span className="flex items-center gap-1"><Ruler className="w-4 h-4" /> {selectedItem.surface}</span>
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {selectedItem.date}</span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="luxury-label">Portfolio</span>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
            Nos <span className="text-gold">Réalisations</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-xl mx-auto text-sm sm:text-base">
            Survolez pour voir l'état avant • <span className="text-gold">Cliquez pour agrandir</span>
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 sm:px-6 py-2 rounded-sm text-xs sm:text-sm font-medium transition-all ${
                filter === f.id
                  ? 'bg-gold text-black'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredGallery.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="group relative cursor-pointer"
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => setSelectedItem(item)}
              data-testid={`gallery-item-${item.id}`}
            >
              <div className="luxury-card overflow-hidden">
                <div className="relative aspect-video sm:aspect-[4/3] overflow-hidden">
                  <motion.img
                    src={hoveredItem === item.id ? item.before_image : item.after_image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    animate={{ scale: hoveredItem === item.id ? 1.1 : 1 }}
                    transition={{ duration: 0.5 }}
                  />
                  
                  {/* Overlay (less intense on mobile) */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent sm:from-black/80 sm:via-black/20" />
                  
                  {/* Badge */}
                  <motion.div 
                    className={`absolute sm:top-3 top-auto bottom-3 sm:bottom-auto left-3 sm:left-4 px-3 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-bold flex items-center gap-2 ${
                      hoveredItem === item.id ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                    }`}
                    animate={{ scale: hoveredItem === item.id ? [1, 1.1, 1] : 1 }}
                  >
                    <ArrowLeftRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    {hoveredItem === item.id ? 'AVANT' : 'APRÈS'}
                  </motion.div>

                  {/* Service Type Badge */}
                  {(() => {
                    const st = SERVICE_TYPE_MAP[item.service_type] || { label: item.service_type, classes: 'bg-gray-600 text-white' };
                    return (
                      <div className={`absolute sm:top-3 top-auto bottom-3 sm:bottom-auto right-3 sm:right-4 px-2 sm:px-3 py-1 rounded text-xs font-medium uppercase ${st.classes}`}>
                        {st.label}
                      </div>
                    );
                  })()}

                  {/* Click indicator */}
                  <div className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <motion.div 
                      className="bg-gold/90 rounded-full p-3"
                      initial={{ scale: 0 }}
                      whileHover={{ scale: 1 }}
                      animate={{ scale: hoveredItem === item.id ? 1 : 0 }}
                    >
                      <span className="text-black font-bold text-sm">VOIR</span>
                    </motion.div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-4 sm:p-6">
                  <h3 className="font-serif text-base sm:text-xl text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-xs sm:text-sm mb-4">{item.description}</p>
                  
                  {/* Meta Info */}
                  <div className="flex flex-wrap gap-3 sm:gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {item.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Ruler className="w-3 h-3" />
                      {item.surface}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.date}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredGallery.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500">Aucune réalisation trouvée pour ce filtre.</p>
          </div>
        )}
      </div>

      <div className="mt-24">
        <Footer />
      </div>
    </div>
  );
};

export default GalleryPage;
